import { defaultContentProps } from "../classes/defaultContentProps";
import { EditorV3Line } from "../classes/EditorV3Line";
import { EditorV3ContentPropsInput, IEditorV3 } from "../classes/interface";
import { stringToV3 } from "./stringToV3";

/**
 * Parses an HTML div element and converts it to EditorV3 content structure.
 * Reads child nodes and constructs EditorV3 lines with appropriate text blocks and formatting.
 *
 * @param div - The HTML div element containing EditorV3 content
 * @param contentProps - The content properties for parsing the HTML
 * @returns An IEditorV3 object representing the parsed content
 */
export const readV3Html = (
  div: HTMLDivElement,
  contentProps: EditorV3ContentPropsInput,
): IEditorV3 => {
  const ret: { lines: EditorV3Line[]; contentProps: EditorV3ContentPropsInput } = {
    lines: [],
    contentProps,
  };

  // Set up line counter
  let currentLine = 0;

  // Loop through all child nodes of the div
  div.childNodes.forEach((node) => {
    // Handle aiev3-line div elements
    /* istanbul ignore else */
    if (node.nodeType === Node.ELEMENT_NODE && node.nodeName === "DIV") {
      const div = node as HTMLDivElement;
      let line = new EditorV3Line("");
      /* istanbul ignore else */
      if (div.classList.contains("aiev3-line")) {
        line = new EditorV3Line(div);
      } else if (div.classList.contains("aiev3-markdown-line")) {
        line = new EditorV3Line(div.textContent ?? "", {
          ...defaultContentProps,
          ...ret.contentProps,
          ...contentProps,
        });
      }
      // if the current line is not empty, append it
      if (currentLine < ret.lines.length && ret.lines[currentLine].lineText.length > 0) {
        ret.lines[currentLine] = new EditorV3Line(
          [...ret.lines[currentLine].textBlocks, ...line.textBlocks],
          ret.contentProps,
        );
        currentLine++;
      }
      // otherwise push
      else {
        ret.lines.push(line);
        currentLine++;
      }
    }
    // Handle everything else if it's a text node
    /* istanbul ignore else */
    else if (node.nodeType === Node.TEXT_NODE) {
      const text = (node as Text).textContent ?? "";
      const lines = stringToV3(text).lines.map((l) => new EditorV3Line(l));
      // Push each line to the ret.lines array, do not increment currentLine
      ret.lines.push(...lines);
    }

    // Always take contentProps from the input if it exists
    /* istanbul ignore else */
    if (contentProps) ret.contentProps = contentProps;
  });

  // Trim down to data to return
  return {
    lines: ret.lines.map((l) => l.data),
    contentProps: ret.contentProps,
  };
};
